var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { createApparatus } from "apparatus/library";
import { P, Pivot, Pp, R, RP, SharedColors, SharedGradients } from "apparatus/library/common";
import { t_add } from "i18n_utils";
import { Group, Path, PointText } from "paper";
import { staticImplements } from "types";
const BLOCK_GRADIENT = [
    ["#555555", 0.1],
    ["#333333", 0.15],
];
const BODY_WIDTH = 230;
const BODY_HEIGHT = 90;
const PUMP_HEIGHT = 30;
const LEFT_BLOCK_X = 5;
const LEFT_BLOCK_WIDTH = 25;
const RIGHT_BLOCK_WIDTH = 30;
const RIGHT_BLOCK_X = BODY_WIDTH - 35;
const PUSHER_WIDTH = 16;
const ROD_THICKNESS = 8;
const MIN_PUSHER_X = LEFT_BLOCK_X + LEFT_BLOCK_WIDTH;
const MAX_PUSHER_X = RIGHT_BLOCK_X - PUSHER_WIDTH - 1;
const PUSHER_RANGE = MAX_PUSHER_X - MIN_PUSHER_X;
/**
 * Syringe Pump apparatus.
 */
let SyringePump = class SyringePump {
    constructor() {
        this.drivers = [
            {
                type: "rectangle",
                minX: MIN_PUSHER_X + PUSHER_WIDTH / 2,
                maxX: MAX_PUSHER_X + PUSHER_WIDTH / 2,
                minY: -PUMP_HEIGHT / 2,
                maxY: -PUMP_HEIGHT / 2,
                scale: 1.0,
                fromAppearance: (a) => P(MIN_PUSHER_X + PUSHER_WIDTH / 2 + a.pusherPos * PUSHER_RANGE, -PUMP_HEIGHT / 2),
                toAppearance: (f, p) => {
                    const pos = ((p.x - (MIN_PUSHER_X + PUSHER_WIDTH / 2)) / PUSHER_RANGE).round(2);
                    f({ pusherPos: pos });
                }
            }
        ];
    }
    render(appearance) {
        // Main body
        const body = new Path.Rectangle(R(0, 0, BODY_WIDTH, BODY_HEIGHT, Pivot.TOP_LEFT), 4)
            .withGradientFill("down", [["#9c9bcc", 0.64], ["#6669a4", 0.74]])
            .withStroke("default", SharedColors.stroke);
        const controlPanelBackground = new Path.Rectangle(R(20, 15, BODY_WIDTH - 40, BODY_HEIGHT - 60, Pivot.TOP_LEFT), 4)
            .withFill("#333333");
        // Screen
        const screen = new Path.Rectangle(R(30, 20, 85, 20, Pivot.TOP_LEFT), 2)
            .withFill("#050505");
        const text = new PointText(screen.bounds.bottomLeft.add(P(4, -6)))
            .withFill("#2beec7");
        text.content = appearance.display;
        text.style = {
            fontFamily: "monospace",
            fontSize: "10",
            fontWeight: "bold",
            justification: "left"
        };
        const buttonPanel = new Group([
            new Path.Rectangle(R(0, 0, 14, 14, Pivot.TOP_LEFT), 4).withFill("#e4b745"),
            new Path.Rectangle(R(16, 0, 14, 14, Pivot.TOP_LEFT), 4).withFill("#e4b745"),
            new Path.Rectangle(R(32, 0, 14, 14, Pivot.TOP_LEFT), 4).withFill("#3aab5a"),
            new Path.Rectangle(R(48, 0, 14, 14, Pivot.TOP_LEFT), 4).withFill("#ce4949"),
        ])
            .withPositionDelta({ x: controlPanelBackground.bounds.right - 70, y: 23 });
        const mainBodyGroup = new Group([body, controlPanelBackground, screen, text, buttonPanel]);
        // Pump Group
        const leftBlock = new Path.Rectangle(R(LEFT_BLOCK_X, 0, LEFT_BLOCK_WIDTH, PUMP_HEIGHT - 5, Pivot.BOTTOM_LEFT))
            .withGradientFill("down", BLOCK_GRADIENT)
            .withStroke("default", "black");
        leftBlock.name = "left_block";
        const rightBlock = new Path.Rectangle(R(RIGHT_BLOCK_X, 0, RIGHT_BLOCK_WIDTH, PUMP_HEIGHT - 5, Pivot.BOTTOM_LEFT))
            .withGradientFill("down", BLOCK_GRADIENT)
            .withStroke("default", "black");
        rightBlock.name = "right_block";
        const pusherX = MIN_PUSHER_X + (appearance.pusherPos * PUSHER_RANGE);
        const pusherBlockBody = new Path.Rectangle(R(0, 0, PUSHER_WIDTH, PUMP_HEIGHT, Pivot.BOTTOM_LEFT))
            .withGradientFill("down", BLOCK_GRADIENT)
            .withStroke("default", "black");
        pusherBlockBody.name = "pusher_block_body";
        const pusherBlock = new Group([pusherBlockBody]);
        pusherBlock.addChildren([
            new Path.Circle(P(11, -8), 4)
                .withStroke("thin", "#9e8e10")
                .withFill("#dfbd16"),
            new Path.Circle(P(12, -PUMP_HEIGHT + 3), 7)
                .withStroke("thin", "#111111")
                .withFill("#555555"),
        ]);
        pusherBlock.withPositionDelta({ x: pusherX, y: -2 });
        const rod1_y = -6;
        const rod2_y = -16;
        const rod1 = new Path.Rectangle(RP(10, rod1_y, BODY_WIDTH - 10, rod1_y - ROD_THICKNESS))
            .withGradientFill("down", SharedGradients.cylinderMetal);
        const rod2 = new Path.Rectangle(RP(10, rod2_y, BODY_WIDTH - 10, rod2_y - ROD_THICKNESS))
            .withGradientFill("down", SharedGradients.cylinderMetal);
        const pumpGroup = new Group([rod1, rod2, leftBlock, rightBlock, pusherBlock]);
        // feet
        const feetLeft = new Path.Rectangle(R(20, BODY_HEIGHT, 15, 5, Pivot.TOP_LEFT))
            .withFill("#222");
        const feetRight = new Path.Rectangle(R(BODY_WIDTH - 35, BODY_HEIGHT, 15, 5, Pivot.TOP_LEFT))
            .withFill("#222");
        const graphic = new Group([feetLeft, feetRight, pumpGroup, mainBodyGroup]);
        graphic.scale(appearance.scale);
        return {
            graphic,
            hitShape: graphic.rectHitShape(),
            snapping: [
                graphic.placeable(),
            ]
        };
    }
};
SyringePump.properties = {
    label: "Syringe Pump",
    flippable: true,
    defaultAppearance: {
        scale: 1.0,
        pusherPos: 0.5,
        display: "5.00 ml/h"
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "scale", label: "Scale",
            min: 0.5, max: 2.0, step: 0.01, unit: "%"
        },
        {
            spec: "slider", key: "pusherPos", label: "Pusher position",
            min: 0.0, max: 1.0, step: 0.01,
        },
        {
            spec: "text", key: "display", label: "Display",
            maxLength: 14
        }
    ],
    actions: [
        {
            type: "add",
            icon: "plus",
            label: () => t_add("syringe"),
            action(a, p, r, { anchors }) {
                let apparatus = createApparatus("syringe", Pp(p).add(P(BODY_WIDTH / 2 + 15, -BODY_HEIGHT / 2 - 5)));
                apparatus.rotation = r - 90;
                return apparatus;
            }
        }
    ]
};
SyringePump = __decorate([
    staticImplements()
], SyringePump);
export { SyringePump };
